import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { pickAndUploadImage } from "../../lib/upload";
import { Upload, Trash2, Save, Plus, Image as ImageIcon, X, Loader2, GripVertical } from "lucide-react";

const errText = (d, f) => (typeof d === "string" ? d : f);

/* Reusable image picker — real file upload to object storage */
function ImagePicker({ value, onChange, testid, label }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const url = await pickAndUploadImage(f);
      onChange(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {value ? (
        <img src={value} alt={label} className="w-full max-w-md aspect-video object-cover border border-white/10 mb-3" />
      ) : (
        <div className="w-full max-w-md aspect-video border border-white/10 flex items-center justify-center text-white/30 mb-3"><ImageIcon /></div>
      )}
      <div className="flex flex-wrap gap-3 items-center">
        <label className={`cursor-pointer inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest transition-colors ${uploading ? "text-gold border-gold cursor-wait" : "text-white/70 hover:border-gold hover:text-gold"}`}>
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload from device</>}
          <input type="file" accept="image/*" className="hidden" disabled={uploading} data-testid={`${testid}-file`} onChange={handleFile} />
        </label>
        <input placeholder="…or paste image URL" data-testid={`${testid}-url`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="editorial-input flex-1 min-w-[200px]" />
      </div>
    </div>
  );
}

function SiteImageCard({ title, field, settings, onSaved }) {
  const [val, setVal] = useState(settings[field] || "");
  useEffect(() => { setVal(settings[field] || ""); }, [settings, field]);
  const save = async () => {
    try { const { data } = await api.put("/admin/site-settings", { [field]: val }); toast.success(`${title} updated`); onSaved(data); }
    catch (err) { toast.error(errText(err.response?.data?.detail, "Error")); }
  };
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-6">
      <p className="text-xs uppercase tracking-widest text-gold mb-4">{title}</p>
      <ImagePicker value={val} onChange={setVal} testid={`site-${field}`} label={title} />
      <button onClick={save} disabled={!val} data-testid={`site-${field}-save`} className="mt-4 inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-widest px-5 py-2.5 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><Save size={14} /> Save</button>
    </div>
  );
}

function BeforeAfterSectionCard({ settings, onSaved }) {
  const initial = () => ({
    ba_overline: settings.ba_overline || "",
    ba_title: settings.ba_title || "",
    ba_subtitle: settings.ba_subtitle || "",
    ba_cta_label: settings.ba_cta_label || "",
  });
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial()); }, [settings.ba_overline, settings.ba_title, settings.ba_subtitle, settings.ba_cta_label]); // eslint-disable-line
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try {
      const { data } = await api.put("/admin/site-settings", form);
      toast.success("Homepage Before & After section updated");
      onSaved(data);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, "Error"));
    }
  };
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-6 lg:col-span-2" data-testid="ba-section-card">
      <p className="text-xs uppercase tracking-widest text-gold mb-1">Homepage — Before &amp; After Section</p>
      <p className="text-white/40 text-xs mb-5">Text shown on the homepage above the interactive slider.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">Overline</label>
          <input value={form.ba_overline} onChange={set("ba_overline")} placeholder="Before & After" className="editorial-input" data-testid="ba-overline-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">CTA button label</label>
          <input value={form.ba_cta_label} onChange={set("ba_cta_label")} placeholder="View Before & After" className="editorial-input" data-testid="ba-cta-input" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">Section title (serif heading)</label>
          <input value={form.ba_title} onChange={set("ba_title")} placeholder="See the transformation" className="editorial-input" data-testid="ba-title-input" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">Subtitle / description</label>
          <textarea rows={2} value={form.ba_subtitle} onChange={set("ba_subtitle")} placeholder="Our latest 10 completed projects across Virginia — drag to reveal each one." className="editorial-input resize-none" data-testid="ba-subtitle-input" />
        </div>
      </div>
      <button onClick={save} data-testid="ba-section-save" className="mt-5 inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-widest px-5 py-2.5 hover:bg-white transition-colors">
        <Save size={14} /> Save section text
      </button>
    </div>
  );
}

function ServiceEditor({ service, onChanged }) {
  const [cover, setCover] = useState(service.image || "");
  const [text, setText] = useState({ title: service.title || "", short: service.short || "", description: service.description || "" });
  const [newImg, setNewImg] = useState("");
  useEffect(() => { setCover(service.image || ""); setText({ title: service.title || "", short: service.short || "", description: service.description || "" }); setNewImg(""); }, [service.id]);

  const saveCover = async () => { const { data } = await api.put(`/admin/services/${service.id}`, { image: cover }); toast.success("Cover updated"); onChanged(data); };
  const saveText = async () => { const { data } = await api.put(`/admin/services/${service.id}`, text); toast.success("Details updated"); onChanged(data); };
  const addPhoto = async () => { if (!newImg) return toast.error("Add a file or URL"); const { data } = await api.post(`/admin/services/${service.id}/gallery`, { url: newImg }); toast.success("Photo added"); setNewImg(""); onChanged(data); };
  const removePhoto = async (url) => { const { data } = await api.post(`/admin/services/${service.id}/gallery/remove`, { url }); onChanged(data); };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-widest text-gold mb-3">Cover Image</p>
        <ImagePicker value={cover} onChange={setCover} testid="svc-cover" label="cover" />
        <button onClick={saveCover} data-testid="svc-cover-save" className="mt-4 inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-widest px-5 py-2.5 hover:bg-white transition-colors"><Save size={14} /> Save cover</button>
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-gold mb-3">Details</p>
        <div className="space-y-4 max-w-xl">
          <input value={text.title} onChange={(e) => setText({ ...text, title: e.target.value })} placeholder="Title" className="editorial-input" data-testid="svc-title" />
          <input value={text.short} onChange={(e) => setText({ ...text, short: e.target.value })} placeholder="Short description" className="editorial-input" data-testid="svc-short" />
          <textarea rows={3} value={text.description} onChange={(e) => setText({ ...text, description: e.target.value })} placeholder="Full description" className="editorial-input resize-none" data-testid="svc-desc" />
          <button onClick={saveText} data-testid="svc-text-save" className="inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-widest px-5 py-2.5 hover:bg-white transition-colors"><Save size={14} /> Save details</button>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-gold mb-3">Gallery ({(service.gallery || []).length})</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
          {(service.gallery || []).map((g, i) => (
            <div key={i} className="relative group border border-white/10 aspect-square overflow-hidden" data-testid={`svc-photo-${i}`}>
              <img src={g} alt="" className="h-full w-full object-cover" />
              <button onClick={() => removePhoto(g)} data-testid={`svc-photo-${i}-delete`} aria-label="Delete photo" className="absolute top-1 right-1 bg-black/70 p-1.5 text-white/80 hover:text-red-400 md:opacity-0 md:group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
        <div className="border border-white/10 p-4 max-w-md">
          <p className="text-xs uppercase tracking-widest text-white/50 mb-3">Add photo</p>
          <ImagePicker value={newImg} onChange={setNewImg} testid="svc-newphoto" label="new" />
          <button onClick={addPhoto} data-testid="svc-photo-add" className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-gold hover:text-black text-white text-xs uppercase tracking-widest py-2.5 transition-colors"><Plus size={14} /> Add to gallery</button>
        </div>
      </div>
    </div>
  );
}

function NewServiceModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", short: "", description: "" });
  const [cover, setCover] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/services", {
        title: form.title.trim(),
        short: form.short.trim(),
        description: form.description.trim(),
        image: cover || "",
      });
      toast.success(`Service "${data.title}" created`);
      onCreated(data);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, "Error creating service"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="new-service-modal"
    >
      <div
        className="w-full max-w-2xl border border-white/10 bg-[#0a0a0a] p-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-3xl">
            <span className="gold-gradient-text">New</span> Service
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="new-service-close"
            className="text-white/40 hover:text-gold transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="new-service-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">
              Service title
            </label>
            <input
              required
              autoFocus
              value={form.title}
              onChange={set("title")}
              placeholder="e.g. Outdoor Kitchens"
              className="editorial-input"
              data-testid="new-service-title"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">
              Short description (one line)
            </label>
            <input
              value={form.short}
              onChange={set("short")}
              placeholder="Chef-worthy outdoor kitchens with premium finishes."
              className="editorial-input"
              data-testid="new-service-short"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-2">
              Full description
            </label>
            <textarea
              rows={4}
              value={form.description}
              onChange={set("description")}
              placeholder="Detailed description shown on the service page."
              className="editorial-input resize-none"
              data-testid="new-service-desc"
            />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">
              Cover image (optional — you can add later)
            </p>
            <ImagePicker value={cover} onChange={setCover} testid="new-service-cover" label="cover" />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-black text-xs uppercase tracking-widest py-3.5 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="new-service-submit"
            >
              {submitting ? "Creating…" : <><Plus size={14} /> Create Service</>}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 border border-white/20 text-white/70 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors"
            >
              Cancel
            </button>
          </div>

          <p className="text-white/30 text-xs pt-2">
            After creating, you can add a gallery, materials list, and edit any field.
          </p>
        </form>
      </div>
    </div>
  );
}

export default function AdminContent() {
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const loadSettings = () => api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
  const loadServices = () => api.get("/services").then((r) => { setServices(r.data); setActiveId((id) => id || (r.data[0] && r.data[0].id)); }).catch(() => {});
  useEffect(() => { loadSettings(); loadServices(); }, []);

  const patchService = (updated) => setServices((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  const active = services.find((s) => s.id === activeId);

  const handleCreated = (created) => {
    setServices((prev) => [...prev, created]);
    setActiveId(created.id);
    setShowNew(false);
  };

  const deleteService = async (svc) => {
    if (!window.confirm(`Delete "${svc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/services/${svc.id}`);
      const remaining = services.filter((s) => s.id !== svc.id);
      setServices(remaining);
      if (activeId === svc.id) setActiveId(remaining[0]?.id || null);
      toast.success("Service deleted");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, "Error deleting service"));
    }
  };

  const commitOrder = async (newList) => {
    setServices(newList);
    try {
      await api.post("/admin/services/reorder", { ordered_ids: newList.map((s) => s.id) });
      toast.success("Order updated");
    } catch {
      toast.error("Could not save order — reloading");
      loadServices();
    }
  };

  const onDragStart = (i) => (e) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch {}
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== i) setOverIndex(i);
  };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null); setOverIndex(null);
    if (from === null || from === i) return;
    const next = [...services];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    commitOrder(next);
  };
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  return (
    <div>
      <h2 className="font-serif text-3xl md:text-4xl mb-2">Site Content</h2>
      <p className="text-white/40 text-sm mb-8">Update the images and text shown across your public website.</p>

      <div className="grid lg:grid-cols-2 gap-4 mb-14">
        <SiteImageCard title="Homepage Hero Image" field="hero_image" settings={settings} onSaved={setSettings} />
        <SiteImageCard title="About Page Image" field="about_image" settings={settings} onSaved={setSettings} />
        <BeforeAfterSectionCard settings={settings} onSaved={setSettings} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <h3 className="font-serif text-2xl">Services</h3>
          <p className="text-white/40 text-xs mt-1">{services.length} service{services.length !== 1 && "s"} · drag to reorder — order applies to homepage &amp; /services</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="admin-new-service-btn"
          className="inline-flex items-center gap-2 bg-gold text-black text-xs uppercase tracking-widest px-5 py-2.5 hover:bg-white transition-colors"
        >
          <Plus size={15} /> New Service
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <div className="border border-white/10 divide-y divide-white/10 h-fit max-h-[70vh] overflow-y-auto">
          {services.map((s, i) => {
            const isActive = activeId === s.id;
            const isDragging = dragIndex === i;
            const isOver = overIndex === i && dragIndex !== i;
            return (
              <div
                key={s.id}
                draggable
                onDragStart={onDragStart(i)}
                onDragOver={onDragOver(i)}
                onDrop={onDrop(i)}
                onDragEnd={onDragEnd}
                className={`flex items-center pr-2 transition-all ${isActive ? "bg-gold/10" : "hover:bg-white/[0.03]"} ${isDragging ? "opacity-40" : ""} ${isOver ? "border-t-2 border-gold" : ""}`}
              >
                <span className="cursor-grab active:cursor-grabbing pl-2 pr-1 py-3 text-white/25 hover:text-gold shrink-0" title="Drag to reorder" data-testid={`svc-drag-${s.slug}`}>
                  <GripVertical size={15} />
                </span>
                <button
                  data-testid={`svc-select-${s.slug}`}
                  onClick={() => setActiveId(s.id)}
                  className={`flex-1 min-w-0 text-left py-3 pr-2 text-sm truncate ${isActive ? "text-gold" : "text-white/60 hover:text-white"}`}
                >
                  <span className="text-white/25 mr-2 text-xs">#{i + 1}</span>
                  {s.title}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteService(s); }}
                  data-testid={`svc-delete-${s.slug}`}
                  aria-label={`Delete ${s.title}`}
                  className="text-white/30 hover:text-red-400 p-2 shrink-0 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
          {!services.length && (
            <p className="text-white/40 text-sm p-4">No services yet. Click "New Service" to add one.</p>
          )}
        </div>
        <div className="min-w-0">
          {active ? <ServiceEditor key={active.id} service={active} onChanged={patchService} /> : <p className="text-white/40">Select a service to edit, or create a new one.</p>}
        </div>
      </div>

      {showNew && <NewServiceModal onClose={() => setShowNew(false)} onCreated={handleCreated} />}
    </div>
  );
}
